import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  MessageSquare, Send, Bot, User, Loader2, Plus, ChevronLeft, 
  Scale, Calculator, Factory, Home, Wrench, MapPin, Star, TrendingUp, Shield,
  Settings, History, Sparkles, ChevronDown, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Agent icons mapping
const AGENT_ICONS = {
  legal: Scale,
  fiscal: Calculator,
  industrial: Factory,
  residencial: Home,
  tecnico: Wrench,
  regional: MapPin,
  atencion_premium: Star,
  ventas: TrendingUp,
  etica: Shield
};

// Agent colors
const AGENT_COLORS = {
  legal: 'bg-blue-500',
  fiscal: 'bg-green-500',
  industrial: 'bg-orange-500',
  residencial: 'bg-purple-500',
  tecnico: 'bg-gray-500',
  regional: 'bg-red-500',
  atencion_premium: 'bg-yellow-500',
  ventas: 'bg-emerald-500',
  etica: 'bg-indigo-500'
};

export default function UpimIAChatPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Chat state
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  
  // Agents and context
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('atencion_premium');
  const [contextOptions, setContextOptions] = useState({ states: [], property_types: [], operation_types: [] });
  const [selectedState, setSelectedState] = useState('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [selectedOperationType, setSelectedOperationType] = useState('');
  
  // UI state
  const [showSidebar, setShowSidebar] = useState(true);
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const [showContextModal, setShowContextModal] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('upim_token');
    if (!storedToken) {
      navigate('/login');
      return;
    }
    setToken(storedToken);
    loadInitialData(storedToken);
  }, [navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadInitialData = async (authToken) => {
    try {
      const [agentsRes, contextRes, conversationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/upim-ia/agents`),
        axios.get(`${API_URL}/api/upim-ia/context-options`),
        axios.get(`${API_URL}/api/upim-ia/conversations`, {
          headers: { Authorization: `Bearer ${authToken}` }
        })
      ]);
      
      setAgents(agentsRes.data.agents || []);
      setContextOptions(contextRes.data);
      setConversations(conversationsRes.data.conversations || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/upim-ia/conversations/${conversationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveConversation(response.data);
      setMessages(response.data.messages || []);
      setSelectedAgent(response.data.agent_type);
      setSelectedState(response.data.mexican_state || '');
      setSelectedPropertyType(response.data.property_type || '');
      setSelectedOperationType(response.data.operation_type || '');
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const startNewConversation = () => {
    setActiveConversation(null);
    setMessages([]);
    setShowAgentSelector(true);
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!inputMessage.trim() || sending) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setSending(true);

    // Add user message to UI immediately
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await axios.post(
        `${API_URL}/api/upim-ia/chat`,
        {
          message: userMessage,
          agent_type: selectedAgent,
          conversation_id: activeConversation?.id || null,
          mexican_state: selectedState || null,
          property_type: selectedPropertyType || null,
          operation_type: selectedOperationType || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update with real messages
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempUserMsg.id);
        return [
          ...filtered,
          {
            id: response.data.user_message.id,
            role: 'user',
            content: response.data.user_message.content,
            created_at: response.data.user_message.created_at
          },
          {
            id: response.data.assistant_message.id,
            role: 'assistant',
            content: response.data.assistant_message.content,
            created_at: response.data.assistant_message.created_at
          }
        ];
      });

      // Update active conversation
      if (!activeConversation) {
        setActiveConversation({ id: response.data.conversation_id });
        loadConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      alert('Error al enviar el mensaje. Por favor intenta de nuevo.');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/upim-ia/conversations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { 
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
    });
  };

  const getAgentInfo = (agentType) => {
    return agents.find(a => a.type === agentType) || { name: 'Asistente', type: agentType };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  const currentAgent = getAgentInfo(selectedAgent);
  const AgentIcon = AGENT_ICONS[selectedAgent] || Bot;

  return (
    <div className="min-h-screen bg-gray-900 flex" data-testid="upim-ia-chat-page">
      {/* Sidebar */}
      <div className={`${showSidebar ? 'w-80' : 'w-0'} bg-gray-800 border-r border-gray-700 transition-all duration-300 overflow-hidden flex flex-col`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-gray-900" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">UPIM IA</h1>
              <p className="text-xs text-gray-400">Altitud Inmobiliaria</p>
            </div>
          </div>
          <button
            onClick={startNewConversation}
            className="w-full flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold py-2.5 px-4 rounded-lg transition-colors"
            data-testid="new-conversation-btn"
          >
            <Plus className="w-4 h-4" />
            Nueva Conversación
          </button>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto p-2">
          <p className="text-xs text-gray-500 uppercase px-2 py-1 mb-2">Conversaciones recientes</p>
          {conversations.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No hay conversaciones</p>
          ) : (
            conversations.map(conv => {
              const ConvIcon = AGENT_ICONS[conv.agent_type] || MessageSquare;
              return (
                <button
                  key={conv.id}
                  onClick={() => loadConversation(conv.id)}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${
                    activeConversation?.id === conv.id 
                      ? 'bg-gray-700 text-white' 
                      : 'hover:bg-gray-700/50 text-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <ConvIcon className="w-4 h-4 mt-0.5 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{conv.title || 'Nueva conversación'}</p>
                      <p className="text-xs text-gray-500">{conv.agent_name}</p>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Chat Header */}
        <div className="h-16 bg-gray-800 border-b border-gray-700 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400"
            >
              <ChevronLeft className={`w-5 h-5 transition-transform ${showSidebar ? '' : 'rotate-180'}`} />
            </button>
            <div className={`w-8 h-8 ${AGENT_COLORS[selectedAgent]} rounded-lg flex items-center justify-center`}>
              <AgentIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-white font-medium">{currentAgent.name}</p>
              <p className="text-xs text-gray-400">{currentAgent.description}</p>
            </div>
          </div>
          <button
            onClick={() => setShowContextModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-gray-300"
            data-testid="context-settings-btn"
          >
            <Settings className="w-4 h-4" />
            Contexto
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4">
          {messages.length === 0 && !activeConversation ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-2xl flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-gray-900" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">UPIM IA - Altitud Inmobiliaria</h2>
              <p className="text-gray-400 mb-6 max-w-md">
                Selecciona un agente especializado y comienza a recibir asesoría profesional inmobiliaria.
              </p>
              
              {/* Agent Grid */}
              <div className="grid grid-cols-3 gap-3 max-w-2xl">
                {agents.map(agent => {
                  const Icon = AGENT_ICONS[agent.type] || Bot;
                  return (
                    <button
                      key={agent.type}
                      onClick={() => {
                        setSelectedAgent(agent.type);
                        inputRef.current?.focus();
                      }}
                      className={`p-4 rounded-xl border transition-all ${
                        selectedAgent === agent.type
                          ? 'border-yellow-500 bg-yellow-500/10'
                          : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                      }`}
                      data-testid={`agent-${agent.type}`}
                    >
                      <div className={`w-10 h-10 ${AGENT_COLORS[agent.type]} rounded-lg flex items-center justify-center mx-auto mb-2`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-medium text-white">{agent.name}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                >
                  {msg.role === 'assistant' && (
                    <div className={`w-8 h-8 ${AGENT_COLORS[selectedAgent]} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <AgentIcon className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-yellow-500 text-gray-900'
                        : 'bg-gray-800 text-gray-100'
                    }`}
                  >
                    <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                    <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-yellow-800' : 'text-gray-500'}`}>
                      {formatDate(msg.created_at)}
                    </p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex gap-3">
                  <div className={`w-8 h-8 ${AGENT_COLORS[selectedAgent]} rounded-lg flex items-center justify-center`}>
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  </div>
                  <div className="bg-gray-800 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Pensando...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-gray-700 bg-gray-800">
          <form onSubmit={sendMessage} className="max-w-3xl mx-auto">
            <div className="flex items-end gap-3">
              <div className="flex-1 bg-gray-700 rounded-xl">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={`Pregunta al ${currentAgent.name}...`}
                  className="w-full bg-transparent text-white placeholder-gray-400 px-4 py-3 rounded-xl resize-none focus:outline-none"
                  rows={1}
                  style={{ maxHeight: '120px' }}
                  data-testid="chat-input"
                />
              </div>
              <button
                type="submit"
                disabled={!inputMessage.trim() || sending}
                className="p-3 bg-yellow-500 hover:bg-yellow-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 rounded-xl transition-colors"
                data-testid="send-message-btn"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              UPIM IA proporciona asesoría general. Para decisiones importantes, consulta con profesionales certificados.
            </p>
          </form>
        </div>
      </div>

      {/* Context Modal */}
      {showContextModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Configurar Contexto</h3>
              <button onClick={() => setShowContextModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Agent Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Agente</label>
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600 focus:border-yellow-500 focus:outline-none"
                >
                  {agents.map(agent => (
                    <option key={agent.type} value={agent.type}>{agent.name}</option>
                  ))}
                </select>
              </div>

              {/* State Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Estado (Región)</label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600 focus:border-yellow-500 focus:outline-none"
                  data-testid="state-selector"
                >
                  <option value="">-- Sin especificar --</option>
                  {contextOptions.states?.map(state => (
                    <option key={state.value} value={state.value}>{state.label} ({state.region})</option>
                  ))}
                </select>
              </div>

              {/* Property Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Inmueble</label>
                <select
                  value={selectedPropertyType}
                  onChange={(e) => setSelectedPropertyType(e.target.value)}
                  className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600 focus:border-yellow-500 focus:outline-none"
                  data-testid="property-type-selector"
                >
                  <option value="">-- Sin especificar --</option>
                  {contextOptions.property_types?.map(pt => (
                    <option key={pt.value} value={pt.value}>{pt.label}</option>
                  ))}
                </select>
              </div>

              {/* Operation Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Operación</label>
                <select
                  value={selectedOperationType}
                  onChange={(e) => setSelectedOperationType(e.target.value)}
                  className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600 focus:border-yellow-500 focus:outline-none"
                  data-testid="operation-type-selector"
                >
                  <option value="">-- Sin especificar --</option>
                  {contextOptions.operation_types?.map(ot => (
                    <option key={ot.value} value={ot.value}>{ot.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={() => setShowContextModal(false)}
              className="w-full mt-6 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold py-2.5 rounded-lg transition-colors"
            >
              Aplicar Contexto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
